import type { KeyboardEvent, ReactNode } from 'react'
import { useToastOptional } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

/**
 * An identifier shown verbatim — a job or payment id, a model code, a wallet address, an env
 * name (WP-14, design §3.1). Onest with tabular figures, not a monospace face: «Моноширинного
 * шрифта в проекте нет». A quiet surface-3 chip; a click (or Enter / Space) selects it and
 * copies it with the toast «Скопировано».
 *
 * `value` is what gets copied when the chip shows a shortened form (`3f2a9c1e…` copies the whole
 * id). `wrap` lets a long value (a UUID, a URL, an address) break anywhere inside its column
 * instead of widening the table. The value is shown as is (`data-raw`, exempt from the
 * number-format check: an id is not an amount).
 */
export function Code({
  children,
  value,
  copy = true,
  toast: toastText = 'Скопировано',
  wrap = false,
  label,
  className,
}: {
  children: ReactNode
  /** the full text to copy (defaults to the children when they are a string) */
  value?: string
  copy?: boolean
  /** the toast after copying, e.g. «Адрес скопирован» */
  toast?: string
  wrap?: boolean
  /** what this is, for the button's name: «id заявки» → «Скопировать id заявки» */
  label?: string
  className?: string
}) {
  const toast = useToastOptional()
  const text = value ?? (typeof children === 'string' ? children : undefined)
  const base = cn(
    'tnum rounded-xs bg-surface-3 px-1.5 text-caption text-ink-2 select-all',
    wrap ? 'break-all [box-decoration-break:clone]' : 'whitespace-nowrap',
    className,
  )

  if (!copy || text === undefined) {
    return (
      <code className={base} data-raw>
        {children}
      </code>
    )
  }

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(text ?? '')
      toast?.success(toastText)
    } catch {
      toast?.error('Не удалось скопировать: выделите вручную')
    }
  }

  function onKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    void onCopy()
  }

  return (
    <code
      role="button"
      tabIndex={0}
      title={label ? `Скопировать ${label}` : 'Скопировать'}
      aria-label={label ? `${text}, скопировать ${label}` : undefined}
      className={cn(base, 'cursor-copy hover:text-ink-1 focus-visible:outline-2 focus-visible:outline-cyan')}
      onClick={(e) => {
        e.stopPropagation() // inside a clickable row: copying does not open the drawer
        void onCopy()
      }}
      onKeyDown={onKey}
      data-raw
    >
      {children}
    </code>
  )
}

/** A link inside admin text (WP-14): ink-1 with a quiet underline that turns cyan on hover —
 *  not small cyan text (neon only from 17 px / 600, rule 18). */
export const LINK_CLASS =
  'text-ink-1 underline decoration-hairline-strong underline-offset-4 hover:decoration-cyan focus-visible:outline-2 focus-visible:outline-cyan'
